import { useMutation } from "@tanstack/react-query";

import { useTranslate } from "@similarweb/i18n-provider";
import type { UserdataActivationSubscriptionsAllCreateParams } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/common/hooks";
import { swSettings } from "app/common/services/swSettings";
import { apiClient } from "app/modules/api-client";
import { SUBSCRIBE_TOAST_SUCCESS_TEXT } from "app/pages/activation/components/AlertsSubscription/texts";

import { useCompetitorUpdatesSubscriptionInvalidation } from "./newsAlerts/useCompetitorUpdatesSubscriptionInvalidation";
import { useAlertsRelatedQueriesInvalidation } from "./useAlertsRelatedQueriesInvalidation";

export const useSubscribeToAllAlertsMutation = ({
    subscribeToBenchmark,
}: Pick<UserdataActivationSubscriptionsAllCreateParams, "subscribeToBenchmark"> = {}) => {
    const invalidateAlertsRelatedQueries = useAlertsRelatedQueriesInvalidation();
    const invalidateCompetitorUpdatesSubscription = useCompetitorUpdatesSubscriptionInvalidation();
    const { translate } = useTranslate();
    const { showErrorToast, showSuccessToast } = useToasts();
    const email = swSettings.user?.username as string;

    return useMutation({
        mutationKey: ["SUBSCRIBE_TO_ALL_QUERY"],
        mutationFn: (params: Omit<UserdataActivationSubscriptionsAllCreateParams, "subscribeToBenchmark">) =>
            apiClient.Activation.userdataActivationSubscriptionsAllCreate({
                ...params,
                subscribeToBenchmark,
            }),
        onSuccess: async (_, { activationId }) => {
            await invalidateAlertsRelatedQueries(activationId);
            await invalidateCompetitorUpdatesSubscription(activationId);

            showSuccessToast(translate(SUBSCRIBE_TOAST_SUCCESS_TEXT, { email }));
        },
        onError(error: Error, { activationId }) {
            Sentry.errorTracker({
                e: error,
                message: "Error in Activation.userdataActivationSubscriptionsAllCreate request",
                level: "error",
                params: {
                    extra: {
                        activationId,
                        subscribeToBenchmark,
                    },
                },
            });

            showErrorToast(translate("common.api.error", { error: error?.message }));
        },
    });
};
